import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { PageHeader, FormField } from "@/components/ui-system";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { CreatableCombobox } from "@/components/ui/creatable-combobox";
import { CourierSelect } from "./CourierSelect";
import { toast } from "sonner";
import {
  Plus, Trash2, Loader2, Package, MapPin, User, ShoppingCart, Wallet,
  Truck, CreditCard, Save, AlertTriangle, CheckCircle2,
} from "lucide-react";

const VOLUMETRIC_DIVISOR = 5000;
const MIN_DEAD_WEIGHT = 0.5;

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const round2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

const emptyProduct = () => ({ name: "", sku: "", unit_price: "", qty: 1, discount: "", tax_pct: "", hsn: "" });

const B2CCreateOrder = ({ user, onLogout }) => {
  const navigate = useNavigate();

  const [pickupLocations, setPickupLocations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [walletInfo, setWalletInfo] = useState(null);
  const [pincodeLoading, setPincodeLoading] = useState(false);

  const [pickupLocationId, setPickupLocationId] = useState("");
  const [channelName, setChannelName] = useState(""); // free-entry sales channel/marketplace (name)
  const [referenceNumber, setReferenceNumber] = useState(""); // seller's own order id/reference -> aggregator order_no
  const [buyer, setBuyer] = useState({
    name: "", phone: "", alt_phone: "", email: "",
    address: "", landmark: "", pincode: "", city: "", state: "",
  });
  const [products, setProducts] = useState([emptyProduct()]);
  const [paymentMode, setPaymentMode] = useState("prepaid"); // 'cod' | 'prepaid'
  const [pkg, setPkg] = useState({ dead_weight_kg: "0.5", length_cm: "", breadth_cm: "", height_cm: "" });
  const [orderValueOverride, setOrderValueOverride] = useState(""); // blank = auto from products
  const [codAmount, setCodAmount] = useState(""); // amount to collect on delivery (required for COD)

  const [saving, setSaving] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null); // order returned from POST /orders (hosts CourierSelect)

  useEffect(() => {
    axios
      .get(`${API}/pickup-locations/`)
      .then((r) => setPickupLocations(Array.isArray(r.data) ? r.data : []))
      .catch(() => toast.error("Failed to load pickup locations"));
    axios.get(`${API}/wallet/balance`).then((r) => setWalletInfo(r.data)).catch(() => {});
    // Connected sales channels (optional — to tag the order to a channel).
    axios.get(`${API}/b2c/seller/channels`).then((r) => setChannels(r.data?.items || [])).catch(() => {});
  }, []);

  // ---- Products subtotal ----
  // line_total = unit_price*qty; discount subtracts; tax_pct adds on the post-discount line.
  const productLine = useCallback((p) => {
    const price = parseFloat(p.unit_price) || 0;
    const qty = parseInt(p.qty) || 0;
    const discount = parseFloat(p.discount) || 0;
    const taxPct = parseFloat(p.tax_pct) || 0;
    const gross = price * qty;
    const afterDiscount = Math.max(0, gross - discount);
    const withTax = afterDiscount * (1 + taxPct / 100);
    return round2(withTax);
  }, []);

  const orderValue = useMemo(
    () => round2(products.reduce((sum, p) => sum + productLine(p), 0)),
    [products, productLine]
  );
  // Manual override wins when a value is entered; else the computed product sum.
  const hasOverride = orderValueOverride.trim() !== "";
  const effectiveOrderValue = hasOverride ? (parseFloat(orderValueOverride) || 0) : orderValue;

  // ---- Live volumetric + applicable weight ----
  const weights = useMemo(() => {
    const dead = parseFloat(pkg.dead_weight_kg) || 0;
    const l = parseFloat(pkg.length_cm) || 0;
    const b = parseFloat(pkg.breadth_cm) || 0;
    const h = parseFloat(pkg.height_cm) || 0;
    const volumetric = (l * b * h) / VOLUMETRIC_DIVISOR;
    const applicable = Math.max(dead, volumetric, MIN_DEAD_WEIGHT);
    return {
      volumetric: round2(volumetric),
      applicable: round2(applicable),
      applicableSource: applicable === dead && dead >= volumetric ? "dead" : volumetric > dead ? "volumetric" : "floor",
    };
  }, [pkg]);

  const isLowBalance = walletInfo
    ? (walletInfo.enforce_wallet_minimum_for_orders !== false) &&
      walletInfo.balance <= walletInfo.min_wallet_balance
    : false;

  // ---- Handlers ----
  const updateProduct = (i, field, value) => {
    setProducts((prev) => prev.map((p, idx) => (idx === i ? { ...p, [field]: value } : p)));
  };
  const addProduct = () => setProducts((prev) => [...prev, emptyProduct()]);
  const removeProduct = (i) => setProducts((prev) => (prev.length > 1 ? prev.filter((_, idx) => idx !== i) : prev));

  const lookupPincode = async (pincode) => {
    if (pincode.length !== 6) return;
    setPincodeLoading(true);
    try {
      const { data } = await axios.get(`${API}/pickup-locations/pincode/${pincode}`);
      setBuyer((prev) => ({ ...prev, city: data.city || prev.city, state: data.state || prev.state }));
      toast.success("Pincode verified");
    } catch (e) {
      toast.error(errDetail(e, "Invalid pincode"));
    } finally {
      setPincodeLoading(false);
    }
  };

  const validate = () => {
    if (!pickupLocationId) return "Select a pickup location";
    if (!referenceNumber.trim()) return "Order ID / Reference Number is required";
    if (!buyer.name.trim()) return "Buyer name is required";
    if (!/^\d{10}$/.test(buyer.phone)) return "Enter a valid 10-digit buyer phone";
    if (!buyer.address.trim()) return "Buyer address is required";
    if (!/^\d{6}$/.test(buyer.pincode)) return "Enter a valid 6-digit pincode";
    const validProducts = products.filter((p) => p.name.trim() && parseFloat(p.unit_price) > 0 && parseInt(p.qty) > 0);
    if (validProducts.length === 0) return "Add at least one product with name, price and quantity";
    if ((parseFloat(pkg.dead_weight_kg) || 0) < MIN_DEAD_WEIGHT) return `Dead weight must be at least ${MIN_DEAD_WEIGHT} kg`;
    if (!(parseFloat(pkg.length_cm) > 0 && parseFloat(pkg.breadth_cm) > 0 && parseFloat(pkg.height_cm) > 0))
      return "Enter package length, breadth and height";
    if (paymentMode === "cod" && !(codAmount.trim() !== "" && parseFloat(codAmount) >= 0))
      return "Enter the COD amount to collect";
    return null;
  };

  const buildPayload = () => ({
    pickup_location_id: pickupLocationId,
    reference_number: referenceNumber.trim(),
    ...(channelName.trim() ? { channel_name: channelName.trim() } : {}),
    ...(hasOverride ? { order_value: parseFloat(orderValueOverride) } : {}),
    ...(paymentMode === "cod" ? { cod_amount: parseFloat(codAmount) } : {}),
    payment_mode: paymentMode,
    buyer: {
      name: buyer.name.trim(),
      phone: buyer.phone,
      alt_phone: buyer.alt_phone || null,
      email: buyer.email || null,
      address: buyer.address.trim(),
      landmark: buyer.landmark || null,
      pincode: buyer.pincode,
      city: buyer.city,
      state: buyer.state,
    },
    products: products
      .filter((p) => p.name.trim() && parseFloat(p.unit_price) > 0 && parseInt(p.qty) > 0)
      .map((p) => ({
        name: p.name.trim(),
        sku: p.sku || null,
        unit_price: parseFloat(p.unit_price),
        qty: parseInt(p.qty),
        discount: parseFloat(p.discount) || 0,
        tax_pct: parseFloat(p.tax_pct) || 0,
        hsn: p.hsn || null,
      })),
    package: {
      dead_weight_kg: parseFloat(pkg.dead_weight_kg),
      length_cm: parseFloat(pkg.length_cm),
      breadth_cm: parseFloat(pkg.breadth_cm),
      height_cm: parseFloat(pkg.height_cm),
    },
  });

  const saveOrder = async ({ shipAfter }) => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/b2c/seller/orders`, buildPayload());
      // In an auto-ship mode the server books a courier + debits the wallet at
      // creation, so the order comes back already shipped (status past NEW).
      // Land on the shipped confirmation regardless of which button was clicked.
      if (data.status && data.status !== "NEW") {
        toast.success(`Order ${data.order_number} shipped — AWB ${data.awb || "pending"}`);
        setCreatedOrder(data);
      } else if (shipAfter) {
        toast.success(`Order ${data.order_number} saved — choose a courier`);
        setCreatedOrder(data);
      } else {
        toast.success(`Order ${data.order_number} created`);
        navigate("/seller/b2c/orders");
      }
    } catch (e) {
      const status = e?.response?.status;
      const msg = errDetail(e, "Failed to create B2C order");
      if (status === 422 && /plan/i.test(msg)) {
        toast.error("B2C is not enabled or no plan is configured. Contact your account manager.");
      } else {
        toast.error(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  // ---- Courier-select step (after Ship Now) ----
  const quoteInput = useMemo(() => {
    if (!createdOrder) return null;
    return {
      pickup_location_id: pickupLocationId,
      drop_pincode: buyer.pincode,
      payment_mode: paymentMode,
      order_value: effectiveOrderValue,
      package: {
        dead_weight_kg: parseFloat(pkg.dead_weight_kg),
        length_cm: parseFloat(pkg.length_cm),
        breadth_cm: parseFloat(pkg.breadth_cm),
        height_cm: parseFloat(pkg.height_cm),
      },
    };
  }, [createdOrder, pickupLocationId, buyer.pincode, paymentMode, effectiveOrderValue, pkg]);

  // An auto-ship mode books a courier + debits the wallet at creation — the order
  // comes back already shipped, so show a confirmation instead of the courier picker.
  if (createdOrder && createdOrder.status && createdOrder.status !== "NEW") {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="max-w-2xl mx-auto space-y-6">
          <PageHeader
            title="Order shipped"
            subtitle={`Order ${createdOrder.order_number} was booked and shipped automatically.`}
            backTo={() => navigate("/seller/b2c/orders")}
            badge={<Badge className="bg-slate-100 text-slate-600 border-0">{createdOrder.order_number}</Badge>}
          />
          <Card>
            <CardContent className="p-6 space-y-5">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-600">
                  <CheckCircle2 className="h-6 w-6" />
                </span>
                <div>
                  <p className="font-semibold text-slate-800">Shipment booked</p>
                  <p className="text-sm text-slate-500">
                    A courier was auto-assigned per your courier priority setting and your wallet was charged.
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-400">Courier</p>
                  <p className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-slate-700">
                    <Truck className="h-4 w-4 flex-shrink-0 text-slate-400" />
                    <span className="truncate">{createdOrder.courier_name || createdOrder.courier_code || "—"}</span>
                    {createdOrder.courier_mode ? (
                      <span className="text-xs font-normal capitalize text-slate-400">· {createdOrder.courier_mode}</span>
                    ) : null}
                  </p>
                </div>
                <div className="rounded-lg border border-slate-200 p-3">
                  <p className="text-xs text-slate-400">AWB / tracking</p>
                  <p className="mt-0.5 text-sm font-medium text-slate-700" data-testid="b2c-shipped-awb">
                    {createdOrder.awb || "Pending — assigned by the courier shortly"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button variant="outline" onClick={() => window.location.reload()} data-testid="b2c-create-another">
              <Plus className="mr-2 h-4 w-4" /> Create another order
            </Button>
            <div className="flex gap-2">
              <Button variant="ghost" onClick={() => navigate("/seller/b2c/orders")}>
                All orders
              </Button>
              <Button onClick={() => navigate(`/seller/b2c/orders/${createdOrder.id}`)} data-testid="b2c-view-shipped-order">
                View order detail
              </Button>
            </div>
          </div>
        </div>
      </SellerLayout>
    );
  }

  if (createdOrder) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="max-w-5xl mx-auto space-y-6">
          <PageHeader
            title="Select a courier"
            subtitle={`Order ${createdOrder.order_number} is saved. Pick a courier to book the shipment.`}
            backTo={() => navigate("/seller/b2c/orders")}
            badge={<Badge className="bg-slate-100 text-slate-600 border-0">{createdOrder.order_number}</Badge>}
          />
          <CourierSelect
            quoteInput={quoteInput}
            orderId={createdOrder.id}
            walletBalance={walletInfo?.balance}
            onShipped={() => navigate(`/seller/b2c/orders/${createdOrder.id}`)}
          />
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => navigate("/seller/b2c/orders")} data-testid="b2c-ship-later">
              Ship later — go to orders
            </Button>
            <Button variant="ghost" onClick={() => navigate(`/seller/b2c/orders/${createdOrder.id}`)}>
              View order detail
            </Button>
          </div>
        </div>
      </SellerLayout>
    );
  }

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6">
        <PageHeader
          title="Add B2C Order"
          subtitle="Ship a parcel to your buyer's doorstep — COD or prepaid"
          backTo={() => navigate("/seller/b2c/orders")}
        />

        {isLowBalance && (
          <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 p-4" data-testid="b2c-low-balance">
            <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-red-700">Low wallet balance</p>
              <p className="text-red-600">
                Your balance ({inr(walletInfo?.balance)}) is below the minimum. You can save the order, but shipping
                requires a recharge.{" "}
                <button className="underline font-medium" onClick={() => navigate("/seller/wallet")}>
                  Recharge wallet
                </button>
              </p>
            </div>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            saveOrder({ shipAfter: false });
          }}
          className="space-y-6"
        >
          {/* ① Pickup */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MapPin className="w-5 h-5 text-brand" /> Pickup address
              </CardTitle>
              <CardDescription>Where our courier partner collects this parcel.</CardDescription>
            </CardHeader>
            <CardContent>
              <FormField label="Pickup location" htmlFor="b2c-pickup" required>
                {pickupLocations.length === 0 ? (
                  <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between rounded-lg border border-dashed border-slate-200 p-4">
                    <span className="text-sm text-slate-500">No pickup addresses yet.</span>
                    <Button type="button" variant="outline" size="sm" onClick={() => navigate("/seller/pickup-locations")}>
                      <Plus className="w-4 h-4 mr-1" /> Add pickup address
                    </Button>
                  </div>
                ) : (
                  <SearchableSelect
                    value={pickupLocationId}
                    onValueChange={setPickupLocationId}
                    placeholder="Select pickup location"
                    emptyText="No pickup locations found."
                    options={pickupLocations.map((loc) => ({
                      value: loc.id,
                      label: `${loc.facility_name || loc.company_name || "Pickup"} - ${loc.city || ""}`,
                      searchLabel: `${loc.facility_name || ""} ${loc.city || ""} ${loc.pincode || ""} ${loc.address_line || ""}`,
                    }))}
                  />
                )}
              </FormField>
              <FormField
                label="Order ID / Reference Number"
                htmlFor="b2c-reference"
                required
                hint="Your own order id — sent to the courier as the order reference (instead of our internal id)."
              >
                <Input
                  id="b2c-reference"
                  data-testid="b2c-reference"
                  value={referenceNumber}
                  onChange={(e) => setReferenceNumber(e.target.value)}
                  placeholder="e.g. your store order #1024"
                  maxLength={64}
                />
              </FormField>
              <FormField label="Sales channel / marketplace" htmlFor="b2c-channel" hint="Where the order came from (e.g. Amazon, Flipkart, your website). Pick an existing one or type a new name to add it. Optional.">
                <CreatableCombobox
                  value={channelName}
                  onValueChange={setChannelName}
                  placeholder="Direct / none — or pick / type a channel"
                  createLabel="Add channel"
                  data-testid="b2c-channel"
                  options={channels.map((c) => ({ value: c.id, label: c.name }))}
                />
              </FormField>
            </CardContent>
          </Card>

          {/* ② Buyer */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <User className="w-5 h-5 text-brand" /> Delivery details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Buyer name" htmlFor="buyer-name" required>
                  <Input id="buyer-name" data-testid="buyer-name" value={buyer.name}
                    onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} />
                </FormField>
                <FormField label="Mobile number" htmlFor="buyer-phone" required>
                  <Input id="buyer-phone" data-testid="buyer-phone" inputMode="numeric" placeholder="10-digit mobile"
                    value={buyer.phone}
                    onChange={(e) => setBuyer({ ...buyer, phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                </FormField>
              </div>
              <FormField label="Address" htmlFor="buyer-address" required>
                <Input id="buyer-address" data-testid="buyer-address" value={buyer.address}
                  onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} placeholder="Flat / house, street, area" />
              </FormField>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Landmark" htmlFor="buyer-landmark">
                  <Input id="buyer-landmark" data-testid="buyer-landmark" value={buyer.landmark}
                    onChange={(e) => setBuyer({ ...buyer, landmark: e.target.value })} />
                </FormField>
                <FormField label="Pincode" htmlFor="buyer-pincode" required hint="City and state auto-fill on a valid pincode.">
                  <div className="relative">
                    <Input id="buyer-pincode" data-testid="buyer-pincode" inputMode="numeric" maxLength={6}
                      value={buyer.pincode}
                      onChange={(e) => {
                        const v = e.target.value.replace(/\D/g, "").slice(0, 6);
                        setBuyer({ ...buyer, pincode: v });
                        if (v.length === 6) lookupPincode(v);
                      }} />
                    {pincodeLoading && <Loader2 className="absolute right-3 top-2.5 w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                </FormField>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="City" htmlFor="buyer-city">
                  <Input id="buyer-city" data-testid="buyer-city" value={buyer.city}
                    onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} />
                </FormField>
                <FormField label="State" htmlFor="buyer-state">
                  <Input id="buyer-state" data-testid="buyer-state" value={buyer.state}
                    onChange={(e) => setBuyer({ ...buyer, state: e.target.value })} />
                </FormField>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Alternate phone" htmlFor="buyer-alt-phone">
                  <Input id="buyer-alt-phone" data-testid="buyer-alt-phone" inputMode="numeric"
                    value={buyer.alt_phone}
                    onChange={(e) => setBuyer({ ...buyer, alt_phone: e.target.value.replace(/\D/g, "").slice(0, 10) })} />
                </FormField>
                <FormField label="Email" htmlFor="buyer-email">
                  <Input id="buyer-email" data-testid="buyer-email" type="email" value={buyer.email}
                    onChange={(e) => setBuyer({ ...buyer, email: e.target.value })} />
                </FormField>
              </div>
            </CardContent>
          </Card>

          {/* ③ Products */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5 text-brand" /> Products
                </CardTitle>
                <CardDescription>Order value is computed from these lines.</CardDescription>
              </div>
              <Button type="button" variant="outline" size="sm" onClick={addProduct} data-testid="b2c-add-product">
                <Plus className="w-4 h-4 mr-1" /> Add product
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {products.map((p, i) => (
                <div key={i} className="rounded-lg border border-slate-200 p-4 space-y-3" data-testid={`b2c-product-${i}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-slate-700">Item {i + 1}</span>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-slate-500">Line: <span className="font-semibold text-slate-700 tabular-nums">{inr(productLine(p))}</span></span>
                      {products.length > 1 && (
                        <Button type="button" variant="ghost" size="icon" className="text-red-500 hover:bg-red-50"
                          onClick={() => removeProduct(i)} data-testid={`b2c-remove-product-${i}`} aria-label="Remove item">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField label="Product name" htmlFor={`p-name-${i}`} required>
                      <Input id={`p-name-${i}`} value={p.name} onChange={(e) => updateProduct(i, "name", e.target.value)} />
                    </FormField>
                    <FormField label="SKU" htmlFor={`p-sku-${i}`}>
                      <Input id={`p-sku-${i}`} value={p.sku} onChange={(e) => updateProduct(i, "sku", e.target.value)} />
                    </FormField>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
                    <FormField label="Unit price (₹)" htmlFor={`p-price-${i}`} required>
                      <Input id={`p-price-${i}`} type="number" step="0.01" min="0" inputMode="decimal" value={p.unit_price}
                        onChange={(e) => updateProduct(i, "unit_price", e.target.value)} />
                    </FormField>
                    <FormField label="Qty" htmlFor={`p-qty-${i}`} required>
                      <Input id={`p-qty-${i}`} type="number" min="1" inputMode="numeric" value={p.qty}
                        onChange={(e) => updateProduct(i, "qty", e.target.value)} />
                    </FormField>
                    <FormField label="Discount (₹)" htmlFor={`p-disc-${i}`}>
                      <Input id={`p-disc-${i}`} type="number" step="0.01" min="0" inputMode="decimal" value={p.discount}
                        onChange={(e) => updateProduct(i, "discount", e.target.value)} />
                    </FormField>
                    <FormField label="Tax %" htmlFor={`p-tax-${i}`}>
                      <Input id={`p-tax-${i}`} type="number" step="0.01" min="0" inputMode="decimal" value={p.tax_pct}
                        onChange={(e) => updateProduct(i, "tax_pct", e.target.value)} />
                    </FormField>
                    <FormField label="HSN" htmlFor={`p-hsn-${i}`}>
                      <Input id={`p-hsn-${i}`} value={p.hsn} onChange={(e) => updateProduct(i, "hsn", e.target.value)} />
                    </FormField>
                  </div>
                </div>
              ))}
              <div className="flex flex-col gap-3 border-t pt-3 sm:flex-row sm:items-end sm:justify-between">
                <FormField
                  label="Override order value (optional)"
                  htmlFor="b2c-order-value-override"
                  hint="Leave blank to use the calculated value. Enter an amount to set the order value manually."
                  className="sm:max-w-xs"
                >
                  <Input
                    id="b2c-order-value-override"
                    data-testid="b2c-order-value-override"
                    type="number" step="0.01" min="0" inputMode="decimal"
                    value={orderValueOverride}
                    onChange={(e) => setOrderValueOverride(e.target.value)}
                    placeholder={`Auto: ${inr(orderValue)}`}
                  />
                </FormField>
                <div className="text-right">
                  <span className="text-sm text-slate-500">Order value (incl. tax): </span>
                  <span className="text-xl font-heading font-bold text-slate-800 tabular-nums" data-testid="b2c-order-value">
                    {inr(effectiveOrderValue)}
                  </span>
                  {hasOverride && <div className="text-xs text-amber-600">Manual override (calculated: {inr(orderValue)})</div>}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* ④ Payment */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-brand" /> Payment mode
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { id: "prepaid", title: "Prepaid", desc: "Buyer has already paid." },
                  { id: "cod", title: "Cash on Delivery", desc: "Collect the amount below on delivery." },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    data-testid={`b2c-payment-${opt.id}`}
                    onClick={() => {
                      setPaymentMode(opt.id);
                      if (opt.id === "cod" && !codAmount) setCodAmount(String(effectiveOrderValue || ""));
                    }}
                    className={`flex items-start gap-3 rounded-lg border-2 px-5 py-4 text-left transition-all ${
                      paymentMode === opt.id
                        ? "border-brand bg-brand-bg text-brand"
                        : "border-slate-200 text-slate-600 hover:border-slate-300"
                    }`}
                  >
                    <Wallet className="w-5 h-5 mt-0.5 shrink-0" />
                    <div>
                      <div className="font-semibold text-sm">{opt.title}</div>
                      <div className="text-sm opacity-70">{opt.desc}</div>
                    </div>
                  </button>
                ))}
              </div>
              {paymentMode === "cod" && (
                <div className="mt-4">
                  <FormField
                    label="COD amount to collect (₹)"
                    htmlFor="b2c-cod-amount"
                    required
                    hint="What the courier collects on delivery — can differ from the order value if the buyer prepaid part of it."
                  >
                    <Input
                      id="b2c-cod-amount"
                      data-testid="b2c-cod-amount"
                      type="number" step="0.01" min="0" inputMode="decimal"
                      value={codAmount}
                      onChange={(e) => setCodAmount(e.target.value)}
                      placeholder={`e.g. ${inr(effectiveOrderValue)}`}
                      className="sm:max-w-xs"
                    />
                  </FormField>
                </div>
              )}
            </CardContent>
          </Card>

          {/* ⑤ Package */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="w-5 h-5 text-brand" /> Package
              </CardTitle>
              <CardDescription>Charged weight is the higher of dead weight and volumetric weight.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <FormField label="Dead weight (kg)" htmlFor="pkg-dead" required hint={`Min ${MIN_DEAD_WEIGHT} kg`}>
                  <Input id="pkg-dead" data-testid="pkg-dead-weight" type="number" step="0.01" min={MIN_DEAD_WEIGHT} inputMode="decimal"
                    value={pkg.dead_weight_kg} onChange={(e) => setPkg({ ...pkg, dead_weight_kg: e.target.value })} />
                </FormField>
                <FormField label="Length (cm)" htmlFor="pkg-l" required>
                  <Input id="pkg-l" data-testid="pkg-length" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.length_cm} onChange={(e) => setPkg({ ...pkg, length_cm: e.target.value })} />
                </FormField>
                <FormField label="Breadth (cm)" htmlFor="pkg-b" required>
                  <Input id="pkg-b" data-testid="pkg-breadth" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.breadth_cm} onChange={(e) => setPkg({ ...pkg, breadth_cm: e.target.value })} />
                </FormField>
                <FormField label="Height (cm)" htmlFor="pkg-h" required>
                  <Input id="pkg-h" data-testid="pkg-height" type="number" step="0.1" min="0" inputMode="decimal"
                    value={pkg.height_cm} onChange={(e) => setPkg({ ...pkg, height_cm: e.target.value })} />
                </FormField>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">Dead weight</p>
                  <p className="text-lg font-heading font-semibold tabular-nums text-slate-700">
                    {round2(parseFloat(pkg.dead_weight_kg) || 0)} kg
                  </p>
                </div>
                <div className="rounded-lg bg-slate-50 p-4">
                  <p className="text-xs text-slate-500">Volumetric weight</p>
                  <p className="text-lg font-heading font-semibold tabular-nums text-slate-700" data-testid="pkg-volumetric">
                    {weights.volumetric} kg
                  </p>
                </div>
                <div className="rounded-lg border-2 border-brand bg-brand-bg p-4" data-testid="pkg-applicable">
                  <p className="text-xs text-brand font-medium">Applicable weight</p>
                  <p className="text-lg font-heading font-bold tabular-nums text-brand">{weights.applicable} kg</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* CTAs */}
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">
            <Button type="button" variant="outline" onClick={() => navigate("/seller/b2c/orders")} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" variant="outline" disabled={saving} data-testid="b2c-save-order">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Order
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={() => saveOrder({ shipAfter: true })}
              data-testid="b2c-ship-now"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Truck className="w-4 h-4 mr-2" />}
              Ship Now
            </Button>
          </div>
        </form>
      </div>
    </SellerLayout>
  );
};

export default B2CCreateOrder;
